import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    ArrowDown,
    ArrowUp,
    ChevronLeft,
    ChevronRight,
    LayoutGrid,
    List,
    Plus,
    Search,
    Trash2,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { PremiumBadge } from '../../../../components/PremiumBadge';
import { UpgradeNoticeSnackbar } from '../../../../components/UpgradeNoticeSnackbar';
import { LimitBadge } from '../../../../components/LimitBadge';
import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar-icon.svg';
import { PageContainer } from '../../../../common/components/PageContainer';
import { useVendorPlan } from '../../../../common/hooks/use-vendor-plan.hook';
import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { SocialAccount, SocialPost, SocialPlatform, SocialPostStatus } from '../../../../common/models/social-scheduler.model';
import { AccountCard } from '../../../../modules/vendor/social-scheduler/components/AccountCard';
import { PostCard } from '../../../../modules/vendor/social-scheduler/components/PostCard';
import { PlatformIcon, platformConfig } from '../../../../modules/vendor/social-scheduler/components/PlatformIcon';
import { formatPostTimeShort } from '../../../../modules/vendor/social-scheduler/format-time';
import { PostCalendar } from '../../../../modules/vendor/social-scheduler/components/PostCalendar';
import { ConnectAccountDialog } from '../../../../modules/vendor/social-scheduler/components/ConnectAccountDialog';
import { PinterestConnectCard } from '../../../../modules/vendor/pinterest';

const MONTH_KEYS = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

const TAB_MAP: Record<string, number> = { calendar: 0, posts: 1, accounts: 2 };

/** One look for every filter pill on this page (status segments, platform filter, expand-reach). */
const pillClass = (active: boolean) => cn(
    'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active
        ? 'border-foreground bg-accent text-foreground'
        : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
);

/** Dashed framing shared by every empty state on this page. */
const emptyBoxClass = 'border-2 border-dashed px-6 py-12 text-center text-sm text-muted-foreground';

// Status chip token classes for the compact list view.
const listStatusClasses: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground',
    scheduled: 'bg-muted text-foreground',
    on_hold: 'bg-warning/10 text-warning',
    publishing: 'bg-warning/10 text-warning',
    published: 'bg-success/10 text-success',
    failed: 'bg-destructive/10 text-destructive',
};

export const SocialSchedulerPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const api = useSocialSchedulerApi();
    const csApi = useContentStudioApi();

    // Sync tab with ?tab= query param (reactive to URL changes from sidebar)
    const tabFromUrl = TAB_MAP[searchParams.get('tab') || ''] ?? 0;
    const [tab, setTab] = useState(tabFromUrl);

    useEffect(() => {
        setTab(tabFromUrl);
    }, [tabFromUrl]);

    // Detect OAuth callback return (?connected=meta&count=N or &error=...)
    useEffect(() => {
        const connected = searchParams.get('connected');
        if (!connected) return;

        const error = searchParams.get('error');
        if (error) {
            setOauthBanner({ type: 'error', message: error });
        } else {
            const count = parseInt(searchParams.get('count') || '0', 10);
            const created = parseInt(searchParams.get('created') || '0', 10);
            const platformName = connected === 'meta' ? 'Meta' : connected === 'pinterest' ? 'Pinterest' : connected;
            const message =
                count === 0
                    ? t('SOCIAL_SCHEDULER.OAUTH_NO_ACCOUNTS_FOUND', { platform: platformName })
                    : created === count
                        ? t('SOCIAL_SCHEDULER.OAUTH_ACCOUNTS_CONNECTED', { count, platform: platformName })
                        : t('SOCIAL_SCHEDULER.OAUTH_ACCOUNTS_SYNCED', { count, platform: platformName, created });
            setOauthBanner({ type: 'success', message });
        }

        // Clear OAuth params from URL so refresh doesn't re-show the banner
        const next = new URLSearchParams(searchParams);
        ['connected', 'count', 'created', 'error'].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });

        // Auto-dismiss after 6s
        const timer = setTimeout(() => setOauthBanner(null), 6000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const [posts, setPosts] = useState<SocialPost[]>([]);
    const [calendarPosts, setCalendarPosts] = useState<SocialPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [connectOpen, setConnectOpen] = useState(false);
    const [connectPlatform, setConnectPlatform] = useState<SocialPlatform | undefined>(undefined);
    // OAuth return banner (from ?connected=meta&count=N or &error=...)
    const [oauthBanner, setOauthBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Asset URL map: assetUuid → url (loaded in bulk from Content Studio)
    const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    const [accountsLoaded, setAccountsLoaded] = useState(false);

    // Account lookup helpers
    const accountByUuid = useMemo(() => {
        const map: Record<string, SocialAccount> = {};
        for (const a of accounts) map[a.uuid] = a;
        return map;
    }, [accounts]);

    // Calendar state
    const now = new Date();
    const [calYear, setCalYear] = useState(now.getFullYear());
    const [calMonth, setCalMonth] = useState(now.getMonth());

    // Selected day for side panel (auto-selects today on first calendar view)
    const [selectedDay, setSelectedDay] = useState<string | null>(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    });

    // Posts for the selected calendar day
    const selectedDayPosts = useMemo(() => {
        if (!selectedDay) return [];
        return calendarPosts.filter((p) => {
            const d = new Date(p.scheduledAt);
            const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return dateStr === selectedDay;
        });
    }, [calendarPosts, selectedDay]);

    // Initial filter values from URL (e.g. ?status=on_hold&accountUuid=xxx)
    const initialStatusFromUrl = searchParams.get('status') || '';
    const initialAccountFromUrl = searchParams.get('accountUuid') || '';

    // Posts tab filter/sort state
    const [statusSegment, setStatusSegment] = useState<string>(initialStatusFromUrl || 'all');
    const [platformFilter, setPlatformFilter] = useState<string>('all');
    const [accountFilter, setAccountFilter] = useState<string>(initialAccountFromUrl);
    const [sortAsc, setSortAsc] = useState(false); // default: newest first
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    // ── Posts tab: computed counts + filtered/sorted posts ─────────
    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = {
            all: 0, upcoming: 0, draft: 0, on_hold: 0, published: 0, failed: 0,
        };
        for (const p of posts) {
            counts.all++;
            if (p.status === 'scheduled') counts.upcoming++;
            else if (p.status === 'draft') counts.draft++;
            else if (p.status === 'on_hold') counts.on_hold++;
            else if (p.status === 'published') counts.published++;
            else if (p.status === 'failed') counts.failed++;
        }
        return counts;
    }, [posts]);

    // Connected platforms from accounts — drives the platform pill bar
    const connectedPlatforms = useMemo(() => {
        return [...new Set(accounts.filter((a) => a.status !== 'revoked').map((a) => a.platform))];
    }, [accounts]);

    // Post count per account (all statuses, non-deleted)
    const postCountByAccount = useMemo(() => {
        const map: Record<string, number> = {};
        for (const p of posts) {
            map[p.accountUuid] = (map[p.accountUuid] || 0) + 1;
        }
        return map;
    }, [posts]);

    // Accounts grouped by platform (active + paused, excluding revoked)
    const accountsByPlatform = useMemo(() => {
        const map: Record<string, SocialAccount[]> = {};
        for (const a of accounts) {
            if (a.status === 'revoked') continue;
            (map[a.platform] = map[a.platform] || []).push(a);
        }
        return map;
    }, [accounts]);

    // All 4 platforms — for showing "add platform" cards for unconnected ones
    // Only platforms with a real publishing provider — TikTok/LinkedIn return when their integrations land.
    const ALL_PLATFORMS: SocialPlatform[] = ['instagram', 'meta'];
    const disconnectedPlatforms = useMemo(() => {
        return ALL_PLATFORMS.filter((p) => !connectedPlatforms.includes(p));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedPlatforms]);

    // Plan cap on connected social accounts. -1 = unlimited; everything else
    // is a positive cap. We only count "live" accounts (active+paused), matching
    // VendorUsageService#getCurrentUsage on the backend so client and server agree.
    const { getLimit } = useVendorPlan();
    const accountsCap = getLimit('maxSocialAccounts');
    const liveAccountsCount = useMemo(
        () => accounts.filter((a) => a.status === 'active' || a.status === 'paused').length,
        [accounts],
    );
    const isAccountsCapEnforced = typeof accountsCap === 'number' && accountsCap >= 0;
    const isAtAccountsCap = isAccountsCapEnforced && liveAccountsCount >= accountsCap;
    const accountsCapTooltip = isAtAccountsCap
        ? t(
              'SOCIAL_SCHEDULER.ACCOUNT_CAP_REACHED',
              'You\'ve reached your plan\'s limit of {{cap}} connected account(s). Upgrade to add more.',
              { cap: accountsCap },
          )
        : '';

    // Plan cap on monthly scheduled posts. The backend Redis counter is the
    // source of truth (see pms-social-scheduler.controller#schedulePost ->
    // VendorUsageService.incrementUsage). For the pre-flight UX gate we
    // approximate: count posts that are currently in a "consumed-the-quota"
    // status (scheduled/publishing/published/failed) AND whose scheduledAt
    // falls in the current calendar month. Drafts don't consume quota.
    // Slight drift vs the Redis counter is acceptable here — the backend's
    // VendorQuotaGuard remains the authoritative block.
    const postsCap = getLimit('maxScheduledPostsPerMonth');
    const scheduledThisMonthCount = useMemo(() => {
        const now = new Date();
        const ym = `${now.getUTCFullYear()}-${String(now.getUTCMonth()).padStart(2, '0')}`;
        const counted: SocialPostStatus[] = ['scheduled', 'publishing', 'published', 'failed'];
        return posts.filter((p) => {
            if (!counted.includes(p.status)) return false;
            const d = new Date(p.scheduledAt);
            const pYm = `${d.getUTCFullYear()}-${String(d.getUTCMonth()).padStart(2, '0')}`;
            return pYm === ym;
        }).length;
    }, [posts]);
    const isPostsCapEnforced = typeof postsCap === 'number' && postsCap >= 0;
    const isAtPostsCap = isPostsCapEnforced && scheduledThisMonthCount >= postsCap;
    const postsCapTooltip = isAtPostsCap
        ? t(
              'SOCIAL_SCHEDULER.POSTS_CAP_REACHED',
              'You\'ve reached your plan\'s limit of {{cap}} scheduled post(s) this month. Upgrade to schedule more.',
              { cap: postsCap },
          )
        : '';

    // Single shared upgrade-notice toast — same UX everywhere a plan cap blocks an action.
    const [upgradeNotice, setUpgradeNotice] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    });

    const openConnectFor = (platform?: SocialPlatform) => {
        // Soft-block at the click site too — even though buttons are disabled
        // when at cap, this catches keyboard navigation / a11y bypasses and any
        // future call site that forgets to gate.
        if (isAtAccountsCap) {
            setUpgradeNotice({ open: true, message: accountsCapTooltip });
            return;
        }
        setConnectPlatform(platform);
        setConnectOpen(true);
    };

    const filteredSortedPosts = useMemo(() => {
        const lc = search.trim().toLowerCase();
        let out = posts.filter((p) => {
            // Segment filter
            if (statusSegment === 'upcoming' && p.status !== 'scheduled') return false;
            if (statusSegment === 'draft' && p.status !== 'draft') return false;
            if (statusSegment === 'on_hold' && p.status !== 'on_hold') return false;
            if (statusSegment === 'published' && p.status !== 'published') return false;
            if (statusSegment === 'failed' && p.status !== 'failed') return false;
            // Platform filter
            if (platformFilter !== 'all' && p.platform !== platformFilter) return false;
            // Account filter
            if (accountFilter && p.accountUuid !== accountFilter) return false;
            // Search: caption, hashtags, account name
            if (lc) {
                const acct = accountByUuid[p.accountUuid]?.accountName || '';
                const hay = `${p.caption || ''} ${(p.hashtags || []).join(' ')} ${acct}`.toLowerCase();
                if (!hay.includes(lc)) return false;
            }
            return true;
        });

        out = [...out].sort((a, b) => {
            const aMs = new Date(a.scheduledAt).getTime();
            const bMs = new Date(b.scheduledAt).getTime();
            return sortAsc ? aMs - bMs : bMs - aMs;
        });
        return out;
    }, [posts, statusSegment, platformFilter, accountFilter, search, sortAsc, accountByUuid]);

    // Carousel index for the side panel — resets when selected day changes
    const [carouselIdx, setCarouselIdx] = useState(0);
    useEffect(() => { setCarouselIdx(0); }, [selectedDay]);
    // Clamp index when posts shrink (e.g. delete)
    useEffect(() => {
        if (selectedDayPosts.length > 0 && carouselIdx >= selectedDayPosts.length) {
            setCarouselIdx(Math.max(0, selectedDayPosts.length - 1));
        }
    }, [selectedDayPosts.length, carouselIdx]);

    const handleTabChange = (_: any, v: number) => {
        setTab(v);
        const tabNames = ['calendar', 'posts', 'accounts'];
        // Every tab writes itself, Calendar included, so all three share one URL
        // shape and the sidebar can light the right one.
        setSearchParams({ tab: tabNames[v] }, { replace: true });
    };

    // Load all ready assets in bulk and build uuid→url map
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadAssetUrls = useCallback(async () => {
        try {
            const res: any = await csApi.listAssets({ status: 'ready', limit: 500 } as any);
            const items = res?.items || res || [];
            const map: Record<string, string> = {};
            for (const a of items) {
                if (a.uuid && a.url) map[a.uuid] = a.url;
            }
            setAssetUrls(map);
        } catch (err) {
            console.error('[SS-DEBUG] Failed to load asset URLs:', err);
        } finally {
            setAssetsLoaded(true);
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadAccounts = useCallback(async () => {
        try {
            const res = await api.listAccounts();
            setAccounts(res.items || []);
        } catch (err) {
            console.error('Failed to load accounts:', err);
        } finally {
            setAccountsLoaded(true);
        }
    }, []);

    // Fetch all non-deleted posts — filtering done client-side for instant UI
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadPosts = useCallback(async () => {
        try {
            const res = await api.listPosts({ limit: 100 });
            setPosts(res.items || []);
        } catch (err) {
            console.error('Failed to load posts:', err);
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadCalendar = useCallback(async () => {
        try {
            const startDate = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`;
            const lastDay = new Date(calYear, calMonth + 1, 0).getDate();
            const endDate = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${lastDay}`;
            const res = await api.getCalendarPosts({ startDate, endDate });
            setCalendarPosts(res || []);
        } catch (err) {
            console.error('Failed to load calendar:', err);
        }
    }, [calYear, calMonth]);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadAccounts(), loadPosts(), loadCalendar(), loadAssetUrls()]).finally(() =>
            setLoading(false),
        );
    }, [loadAccounts, loadPosts, loadCalendar, loadAssetUrls]);

    const handleConnect = async (payload: any) => {
        try {
            await api.connectAccount(payload);
            await loadAccounts();
            setConnectOpen(false);
        } catch (err: any) {
            // The auth-guarded fetch wrapper throws the raw Response on 4xx/5xx.
            // Quota errors come back as 403 with the standard ForbiddenException
            // payload from VendorQuotaGuard: { message, quotaType, current, limit, planName }.
            let message = t(
                'SOCIAL_SCHEDULER.CONNECT_FAILED',
                'Could not connect this account. Please try again.',
            );
            try {
                if (err && typeof err.json === 'function') {
                    const body = await err.json();
                    if (err.status === 403 && body?.quotaType === 'maxSocialAccounts') {
                        message = t(
                            'SOCIAL_SCHEDULER.ACCOUNT_CAP_REACHED',
                            'You\'ve reached your plan\'s limit of {{cap}} connected account(s). Upgrade to add more.',
                            { cap: body.limit },
                        );
                    } else {
                        const raw = body?.message ?? body?.error ?? message;
                        message = Array.isArray(raw) ? raw.join(' • ') : String(raw);
                    }
                } else if (err?.message) {
                    message = String(err.message);
                }
            } catch {
                // body wasn't JSON — keep fallback
            }
            setUpgradeNotice({ open: true, message });
            // eslint-disable-next-line no-console
            console.error('[social-scheduler] connect failed', err);
            // Refresh accounts in case the backend partially created something
            await loadAccounts();
        }
    };

    const handlePause = async (uuid: string) => {
        await api.pauseAccount(uuid);
        await Promise.all([loadAccounts(), loadPosts(), loadCalendar()]);
    };

    const handleResume = async (uuid: string) => {
        await api.resumeAccount(uuid);
        await loadAccounts();
    };

    const handleDisconnect = async (uuid: string) => {
        await api.disconnectAccount(uuid);
        await Promise.all([loadAccounts(), loadPosts(), loadCalendar()]);
    };

    const [pendingDeleteUuid, setPendingDeleteUuid] = useState<string | null>(null);

    const requestDeletePost = (uuid: string) => setPendingDeleteUuid(uuid);

    const confirmDeletePost = async () => {
        const uuid = pendingDeleteUuid;
        setPendingDeleteUuid(null);
        if (!uuid) return;

        try {
            await api.deletePost(uuid);
        } catch (err: any) {
            /*
             * A published post is deleted from the platform first, and the
             * platform can say no — Instagram in particular. The server sends
             * back what it said; swallowing it would leave the vendor pressing
             * delete on a post that never goes away, with no idea why.
             */
            toast.error(
                err?.response?.data?.message
                || err?.message
                || (t('SOCIAL_SCHEDULER.DELETE_FAILED', 'That post could not be deleted.') as string),
                { duration: 10000 },
            );
            return;
        }

        await loadPosts();
        await loadCalendar();
    };

    const prevMonth = () => {
        if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
        else setCalMonth((m) => m - 1);
    };

    const nextMonth = () => {
        if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
        else setCalMonth((m) => m + 1);
    };

    const handleDayClick = (date: string) => {
        // Always select — the side panel should always show the day's content
        setSelectedDay(date);
    };

    const formatSelectedDay = (dateStr: string) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    };

    return (
        <PageContainer
          requiresFeature="socialScheduler"
          breadcrumbs={[
                { label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') },
                {
                    label: t('SIDEBAR.MARKETING.PLANNER', 'Planner'),
                    icon: <CalendarIcon style={{ width: 20, height: 20 }} />,
                },
            ]}
        >
        <div className="mx-auto max-w-[1200px]">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-xl font-medium tracking-tight text-foreground">
                        {t('SOCIAL_SCHEDULER.TITLE', 'Social Scheduler')}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('SOCIAL_SCHEDULER.SUBTITLE', 'Schedule and manage your social media posts')}
                    </p>
                </div>
                <Button
                    variant="outline"
                    className="h-9"
                    onClick={() => {
                        if (isAtPostsCap) {
                            setUpgradeNotice({ open: true, message: postsCapTooltip });
                            return;
                        }
                        navigate('/marketing/social-scheduler/new');
                    }}
                >
                    <Plus />
                    {t('SOCIAL_SCHEDULER.NEW_POST', 'New post')}
                    {isAtPostsCap && <PremiumBadge />}
                </Button>
            </div>

            {/* OAuth return banner */}
            {oauthBanner && (
                <div
                    role="status"
                    className={cn(
                        'mb-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm font-medium',
                        oauthBanner.type === 'success'
                            ? 'border-success/40 bg-success/10 text-success'
                            : 'border-destructive/40 bg-destructive/10 text-destructive',
                    )}
                >
                    <span className="flex items-center gap-2">
                        {oauthBanner.type === 'success' ? '✓' : '⚠'}
                        {oauthBanner.message}
                    </span>
                    <button
                        type="button"
                        onClick={() => setOauthBanner(null)}
                        aria-label={t('SOCIAL_SCHEDULER.DISMISS', 'Dismiss') || 'Dismiss'}
                        className="transition-opacity hover:opacity-70"
                    >
                        <X className="size-4" />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <Tabs value={String(tab)} onValueChange={(v) => handleTabChange(null, Number(v))} className="mb-4">
                <TabsList className="h-9">
                    <TabsTrigger value="0" className="px-3 text-sm">{t('SOCIAL_SCHEDULER.TAB_CALENDAR', 'Calendar')}</TabsTrigger>
                    <TabsTrigger value="1" className="px-3 text-sm">{t('SOCIAL_SCHEDULER.TAB_POSTS', 'Posts')}</TabsTrigger>
                    <TabsTrigger value="2" className="px-3 text-sm">{t('SOCIAL_SCHEDULER.TAB_ACCOUNTS', 'Accounts')}</TabsTrigger>
                </TabsList>
            </Tabs>

            {loading && (
                <div className="py-16 text-center text-sm text-muted-foreground">
                    {t('SOCIAL_SCHEDULER.LOADING', 'Loading...')}
                </div>
            )}

            {/* ── Calendar Tab ─────────────────────────────────────── */}
            {!loading && tab === 0 && (
                <div className="grid items-start gap-4 md:grid-cols-[1fr_380px] md:gap-6">
                    {/* ── Left: calendar ─── */}
                    <div>
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-medium text-foreground">
                                {t(`SOCIAL_SCHEDULER.MONTHS.${MONTH_KEYS[calMonth]}`)} {calYear}
                            </h2>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={prevMonth}
                                    aria-label={t('SOCIAL_SCHEDULER.PREV_MONTH', 'Previous month') || 'Previous month'}
                                >
                                    <ChevronLeft className="rtl:rotate-180" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mx-1"
                                    onClick={() => {
                                        const now = new Date();
                                        setCalYear(now.getFullYear());
                                        setCalMonth(now.getMonth());
                                        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                                        setSelectedDay(today);
                                    }}
                                >
                                    {t('SOCIAL_SCHEDULER.TODAY', 'Today')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={nextMonth}
                                    aria-label={t('SOCIAL_SCHEDULER.NEXT_MONTH', 'Next month') || 'Next month'}
                                >
                                    <ChevronRight className="rtl:rotate-180" />
                                </Button>
                            </div>
                        </div>

                        <PostCalendar
                            year={calYear}
                            month={calMonth}
                            posts={calendarPosts}
                            selectedDay={selectedDay}
                            onDayClick={handleDayClick}
                        />
                    </div>

                    {/* ── Right: side panel ─── */}
                    <div className="rounded-xl border bg-card p-5 md:sticky md:top-6 md:max-h-[calc(100vh-200px)] md:overflow-y-auto">
                        {!selectedDay ? (
                            <div className="px-4 py-12 text-center text-muted-foreground">
                                <div className="mb-2 text-4xl opacity-50">📅</div>
                                <div className="mb-1 text-sm font-medium text-foreground">
                                    {t('SOCIAL_SCHEDULER.SELECT_A_DAY', 'Select a day')}
                                </div>
                                <div className="text-xs">
                                    {t('SOCIAL_SCHEDULER.SELECT_A_DAY_HINT', 'Click any day on the calendar to see its scheduled posts.')}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 flex items-center justify-between">
                                    <div className="text-sm font-medium text-foreground">
                                        {formatSelectedDay(selectedDay)}
                                    </div>
                                    <div className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                        {selectedDayPosts.length} {selectedDayPosts.length === 1 ? t('SOCIAL_SCHEDULER.POST_SINGULAR', 'post') : t('SOCIAL_SCHEDULER.POST_PLURAL', 'posts')}
                                    </div>
                                </div>

                                {selectedDayPosts.length === 0 ? (
                                    <div className={emptyBoxClass}>
                                        <div className="mb-4">
                                            {t('SOCIAL_SCHEDULER.NO_POSTS_THIS_DAY', 'No posts scheduled for this day.')}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/marketing/social-scheduler/new?date=${selectedDay}`)}
                                        >
                                            <Plus />
                                            {t('SOCIAL_SCHEDULER.SCHEDULE_FOR_THIS_DAY', 'Schedule a post')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {/* Single post — no carousel */}
                                        {selectedDayPosts.length === 1 && (
                                            <PostCard
                                                post={selectedDayPosts[0]}
                                                assetUrlMap={assetUrls}
                                                accountName={accountByUuid[selectedDayPosts[0].accountUuid]?.accountName}
                                                accountAvatar={accountByUuid[selectedDayPosts[0].accountUuid]?.accountAvatar}
                                                assetsLoading={!assetsLoaded}
                                                accountLoading={!accountsLoaded}
                                                onClick={(uuid) => navigate(`/marketing/social-scheduler/post/${uuid}`)}
                                                onDelete={requestDeletePost}
                                            />
                                        )}

                                        {/* Multiple posts — carousel */}
                                        {selectedDayPosts.length > 1 && (() => {
                                            const safeIdx = Math.min(carouselIdx, selectedDayPosts.length - 1);
                                            const current = selectedDayPosts[safeIdx];
                                            const prevPost = () => setCarouselIdx((i) => (i - 1 + selectedDayPosts.length) % selectedDayPosts.length);
                                            const nextPost = () => setCarouselIdx((i) => (i + 1) % selectedDayPosts.length);
                                            return (
                                                <div
                                                    onKeyDown={(e: React.KeyboardEvent) => {
                                                        if (e.key === 'ArrowLeft') { e.preventDefault(); prevPost(); }
                                                        else if (e.key === 'ArrowRight') { e.preventDefault(); nextPost(); }
                                                    }}
                                                    tabIndex={0}
                                                    role="region"
                                                    aria-label={t('SOCIAL_SCHEDULER.POSTS_CAROUSEL', 'Posts for selected day') || 'Posts for selected day'}
                                                    className="outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                                >
                                                    {/* Counter + nav arrows */}
                                                    <div className="mb-2 flex items-center justify-between">
                                                        <div className="text-xs font-medium text-muted-foreground">
                                                            {safeIdx + 1} / {selectedDayPosts.length}
                                                        </div>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="outline"
                                                                size="icon-sm"
                                                                onClick={prevPost}
                                                                aria-label={t('SOCIAL_SCHEDULER.PREV_POST', 'Previous post') || 'Previous post'}
                                                            >
                                                                <ChevronLeft className="rtl:rotate-180" />
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="icon-sm"
                                                                onClick={nextPost}
                                                                aria-label={t('SOCIAL_SCHEDULER.NEXT_POST', 'Next post') || 'Next post'}
                                                            >
                                                                <ChevronRight className="rtl:rotate-180" />
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    {/* Current post */}
                                                    <PostCard
                                                        key={current.uuid}
                                                        post={current}
                                                        assetUrlMap={assetUrls}
                                                        accountName={accountByUuid[current.accountUuid]?.accountName}
                                                        accountAvatar={accountByUuid[current.accountUuid]?.accountAvatar}
                                                        assetsLoading={!assetsLoaded}
                                                        accountLoading={!accountsLoaded}
                                                        onClick={(uuid) => navigate(`/marketing/social-scheduler/post/${uuid}`)}
                                                        onDelete={requestDeletePost}
                                                    />

                                                    {/* Dot indicators */}
                                                    <div
                                                        className="mt-3 flex justify-center gap-1.5"
                                                        role="tablist"
                                                        aria-label={t('SOCIAL_SCHEDULER.POST_NAVIGATION', 'Post navigation') || 'Post navigation'}
                                                    >
                                                        {selectedDayPosts.map((_, i) => (
                                                            <button
                                                                key={i}
                                                                type="button"
                                                                onClick={() => setCarouselIdx(i)}
                                                                aria-label={t('SOCIAL_SCHEDULER.GO_TO_POST', { n: i + 1 }) || `Go to post ${i + 1}`}
                                                                aria-current={i === safeIdx}
                                                                className={cn(
                                                                    'h-1.5 rounded-full p-0 transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                                                                    i === safeIdx
                                                                        ? 'w-[18px] bg-foreground'
                                                                        : 'w-1.5 bg-border hover:bg-muted-foreground',
                                                                )}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="mt-1 text-muted-foreground hover:text-foreground"
                                            onClick={() => navigate(`/marketing/social-scheduler/new?date=${selectedDay}`)}
                                        >
                                            <Plus />
                                            {t('SOCIAL_SCHEDULER.ADD_ANOTHER', 'Add another post')}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Posts Tab ─────────────────────────────────────────── */}
            {!loading && tab === 1 && (
                <div>
                    {/* Row 1: Search + count + sort */}
                    <div className="mb-6 flex flex-wrap items-center gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('SOCIAL_SCHEDULER.SEARCH_POSTS', 'Search caption, hashtag, account…') || ''}
                                className="h-9 ps-8 pe-8"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    aria-label={t('SOCIAL_SCHEDULER.CLEAR_SEARCH', 'Clear search') || 'Clear search'}
                                    className="absolute end-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    <X className="size-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex-1" />
                        {/* Plan-limit usage chip (scheduled this month) — matches Content Studio's credits badge */}
                        <LimitBadge
                            current={scheduledThisMonthCount}
                            limit={postsCap}
                            label={t('PLANS.LIMIT_BADGE.POSTS_PER_MONTH', 'posts/mo')}
                            compact
                        />
                        <span className="text-sm text-muted-foreground">
                            {filteredSortedPosts.length === posts.length
                                ? `${posts.length} ${t('SOCIAL_SCHEDULER.POSTS_COUNT_LABEL', 'posts')}`
                                : `${filteredSortedPosts.length} ${t('SOCIAL_SCHEDULER.OF', 'of')} ${posts.length}`}
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setSortAsc((v) => !v)}
                                    aria-label={sortAsc ? (t('SOCIAL_SCHEDULER.SORT_OLDEST_ARIA', 'Sort: oldest first') || 'Sort: oldest first') : (t('SOCIAL_SCHEDULER.SORT_NEWEST_ARIA', 'Sort: newest first') || 'Sort: newest first')}
                                >
                                    {sortAsc ? <ArrowUp /> : <ArrowDown />}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {sortAsc ? t('SOCIAL_SCHEDULER.SORT_OLDEST', 'Oldest first') : t('SOCIAL_SCHEDULER.SORT_NEWEST', 'Newest first')}
                            </TooltipContent>
                        </Tooltip>
                        {/* View mode toggle (matches /catalog page) */}
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            value={viewMode}
                            onValueChange={(v) => v && setViewMode(v as 'grid' | 'list')}
                            aria-label={t('SOCIAL_SCHEDULER.VIEW_MODE', 'View mode') || 'View mode'}
                        >
                            <ToggleGroupItem
                                value="list"
                                aria-label={t('SOCIAL_SCHEDULER.VIEW_LIST_ARIA', 'Compact list view') || 'Compact list view'}
                            >
                                <List className="size-4" />
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="grid"
                                aria-label={t('SOCIAL_SCHEDULER.VIEW_GRID_ARIA', 'Grid view') || 'Grid view'}
                            >
                                <LayoutGrid className="size-4" />
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </div>

                    {/* Row 2: Status segment pills */}
                    <div
                        role="tablist"
                        aria-label={t('SOCIAL_SCHEDULER.FILTER_STATUS_ARIA', 'Filter by status') || 'Filter by status'}
                        className={cn('flex flex-wrap gap-1.5', connectedPlatforms.length > 1 ? 'mb-2' : 'mb-6')}
                    >
                        {[
                            { key: 'all', label: t('SOCIAL_SCHEDULER.SEG_ALL', 'All') },
                            { key: 'upcoming', label: t('SOCIAL_SCHEDULER.SEG_UPCOMING', 'Upcoming') },
                            { key: 'draft', label: t('SOCIAL_SCHEDULER.SEG_DRAFTS', 'Drafts') },
                            { key: 'on_hold', label: t('SOCIAL_SCHEDULER.SEG_ON_HOLD', 'On hold') },
                            { key: 'published', label: t('SOCIAL_SCHEDULER.SEG_PUBLISHED', 'Published') },
                            { key: 'failed', label: t('SOCIAL_SCHEDULER.SEG_FAILED', 'Failed') },
                        ].map(({ key, label }) => {
                            const active = statusSegment === key;
                            const count = statusCounts[key] ?? 0;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => setStatusSegment(key)}
                                    className={pillClass(active)}
                                >
                                    {label}
                                    <span className="min-w-5 bg-muted px-1.5 text-center text-xs leading-[18px] text-muted-foreground">
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Row 3: Platform pills — only if vendor has multiple platforms */}
                    {connectedPlatforms.length > 1 && (
                        <div className="mb-6 flex flex-wrap items-center gap-1.5">
                            <span className="me-1 text-xs text-muted-foreground">
                                {t('SOCIAL_SCHEDULER.PLATFORM_FILTER', 'Platform:')}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPlatformFilter('all')}
                                className={pillClass(platformFilter === 'all')}
                            >
                                {t('SOCIAL_SCHEDULER.ALL', 'All')}
                            </button>
                            {connectedPlatforms.map((p) => {
                                const active = platformFilter === p;
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setPlatformFilter(active ? 'all' : p)}
                                        aria-pressed={active}
                                        aria-label={platformConfig[p as SocialPlatform]?.label || p}
                                        className={pillClass(active)}
                                    >
                                        <PlatformIcon platform={p as SocialPlatform} size={16} />
                                        {platformConfig[p as SocialPlatform]?.label || p}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Active account filter pill (only if set via URL from AccountCard) */}
                    {accountFilter && (
                        <span className="mb-4 inline-flex items-center gap-1.5 border border-foreground bg-accent py-1 ps-2.5 pe-1.5 text-xs font-medium text-foreground">
                            {accountByUuid[accountFilter]?.accountName || t('SOCIAL_SCHEDULER.ACCOUNT_FILTER_FALLBACK', 'Account filter')}
                            <button
                                type="button"
                                onClick={() => {
                                    setAccountFilter('');
                                    setSearchParams({ tab: 'posts' }, { replace: true });
                                }}
                                aria-label={t('SOCIAL_SCHEDULER.CLEAR_ACCOUNT_FILTER', 'Clear account filter') || 'Clear account filter'}
                                className="text-muted-foreground transition-colors hover:text-foreground"
                            >
                                <X className="size-3.5" />
                            </button>
                        </span>
                    )}

                    {/* Results */}
                    {posts.length === 0 ? (
                        <div className={emptyBoxClass}>
                            <div className="mb-4">
                                {t('SOCIAL_SCHEDULER.EMPTY_POSTS', 'No posts yet. Create your first post to get started.')}
                            </div>
                            <Button
                                variant="outline"
                                className="h-9"
                                onClick={() => {
                                    if (isAtPostsCap) {
                                        setUpgradeNotice({ open: true, message: postsCapTooltip });
                                        return;
                                    }
                                    navigate('/marketing/social-scheduler/new');
                                }}
                            >
                                <Plus />
                                {t('SOCIAL_SCHEDULER.NEW_POST', 'New post')}
                                {isAtPostsCap && <PremiumBadge />}
                            </Button>
                        </div>
                    ) : filteredSortedPosts.length === 0 ? (
                        <div className={emptyBoxClass}>
                            <div className="mb-3">
                                {t('SOCIAL_SCHEDULER.NO_MATCHING_POSTS', 'No posts match these filters.')}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setSearch('');
                                    setStatusSegment('all');
                                    setPlatformFilter('all');
                                    setAccountFilter('');
                                    setSearchParams({ tab: 'posts' }, { replace: true });
                                }}
                            >
                                {t('SOCIAL_SCHEDULER.CLEAR_FILTERS', 'Clear filters')}
                            </Button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
                            {filteredSortedPosts.map((post) => (
                                <PostCard
                                    key={post.uuid}
                                    post={post}
                                    assetUrlMap={assetUrls}
                                    accountName={accountByUuid[post.accountUuid]?.accountName}
                                    accountAvatar={accountByUuid[post.accountUuid]?.accountAvatar}
                                    assetsLoading={!assetsLoaded}
                                    accountLoading={!accountsLoaded}
                                    onClick={(uuid) => navigate(`/marketing/social-scheduler/post/${uuid}`)}
                                    onDelete={requestDeletePost}
                                />
                            ))}
                        </div>
                    ) : (
                        // Compact list view
                        <div className="flex flex-col gap-1.5">
                            {filteredSortedPosts.map((post) => {
                                const account = accountByUuid[post.accountUuid];
                                const allUuids = (post.assetUuids?.length ? post.assetUuids : [post.assetUuid]).filter(Boolean);
                                const thumbUrl = allUuids.map((u) => assetUrls[u]).find(Boolean);
                                const statusLabels: Record<string, string> = {
                                    draft: t('SOCIAL_SCHEDULER.STATUS.DRAFT', 'Draft') || 'Draft',
                                    scheduled: t('SOCIAL_SCHEDULER.STATUS.SCHEDULED', 'Scheduled') || 'Scheduled',
                                    on_hold: t('SOCIAL_SCHEDULER.STATUS.ON_HOLD', 'On hold') || 'On hold',
                                    publishing: t('SOCIAL_SCHEDULER.STATUS.PUBLISHING', 'Publishing') || 'Publishing',
                                    published: t('SOCIAL_SCHEDULER.STATUS.PUBLISHED', 'Published') || 'Published',
                                    failed: t('SOCIAL_SCHEDULER.STATUS.FAILED', 'Failed') || 'Failed',
                                };
                                const statusClass = listStatusClasses[post.status] || listStatusClasses.draft;
                                const statusLabel = statusLabels[post.status] || statusLabels.draft;
                                const captionLine = (post.caption || '').replace(/\s+/g, ' ').trim();
                                const hashtagLine = post.hashtags?.length ? post.hashtags.map((h) => `#${h}`).join(' ') : '';
                                const timeLabel = formatPostTimeShort(post.status === 'published' && post.publishedAt ? post.publishedAt : post.scheduledAt);
                                const openPost = () => navigate(`/marketing/social-scheduler/post/${post.uuid}`);
                                return (
                                    // role=button div (not <button>) so the delete control inside stays valid DOM
                                    <div
                                        key={post.uuid}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openPost}
                                        onKeyDown={(e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openPost();
                                            }
                                        }}
                                        className="flex w-full cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 text-start transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                    >
                                        {/* Thumbnail */}
                                        <div
                                            className="relative flex size-12 shrink-0 items-center justify-center bg-muted bg-cover bg-center text-lg text-muted-foreground"
                                            style={{ backgroundImage: thumbUrl ? `url(${thumbUrl})` : undefined }}
                                        >
                                            {!thumbUrl && '🖼'}
                                            {/* Platform logo badge */}
                                            <span className="absolute -end-1 -bottom-1 rounded-full bg-background p-px leading-none">
                                                <PlatformIcon platform={post.platform} size={18} />
                                            </span>
                                        </div>

                                        {/* Caption + account */}
                                        <div className="min-w-0 flex-1">
                                            <div className={cn('truncate text-sm font-medium', captionLine ? 'text-foreground' : 'text-muted-foreground')}>
                                                {captionLine || t('SOCIAL_SCHEDULER.NO_CAPTION', 'No caption')}
                                                {hashtagLine && (
                                                    <span className="ms-1.5 text-muted-foreground">{hashtagLine}</span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                                {account?.accountName || '—'} · {timeLabel}
                                            </div>
                                        </div>

                                        {/* Status chip */}
                                        <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', statusClass)}>
                                            {statusLabel}
                                        </span>

                                        {/* Delete */}
                                        {['draft', 'scheduled', 'failed'].includes(post.status) && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        onClick={(e) => { e.stopPropagation(); requestDeletePost(post.uuid); }}
                                                        aria-label={t('SOCIAL_SCHEDULER.DELETE_POST', 'Delete post') || 'Delete post'}
                                                        className="shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                    >
                                                        <Trash2 className="size-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t('SOCIAL_SCHEDULER.DELETE', 'Delete')}</TooltipContent>
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Accounts Tab ──────────────────────────────────────── */}
            {!loading && tab === 2 && (() => {
                const visibleAccounts = accounts.filter((a) => a.status !== 'revoked');
                const totalPosts = posts.length;
                const isFullyEmpty = visibleAccounts.length === 0;

                return (
                    <div>
                        {/* Summary strip — compact horizontal */}
                        {!isFullyEmpty && (
                            <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border bg-card px-5 py-3 sm:gap-6">
                                {([
                                    { label: t('SOCIAL_SCHEDULER.ACCOUNTS_STAT', 'Accounts'), value: visibleAccounts.length },
                                    { label: t('SOCIAL_SCHEDULER.PLATFORMS_STAT', 'Platforms'), value: `${connectedPlatforms.length} / ${ALL_PLATFORMS.length}` },
                                    { label: t('SOCIAL_SCHEDULER.TOTAL_POSTS_STAT', 'Total posts'), value: totalPosts },
                                ]).map((stat, i) => (
                                    <React.Fragment key={stat.label}>
                                        {i > 0 && <span aria-hidden className="h-7 w-px shrink-0 bg-border" />}
                                        <span className="flex items-baseline gap-1.5">
                                            <span className="text-base font-medium text-foreground">{stat.value}</span>
                                            <span className="text-xs text-muted-foreground">{stat.label.toLowerCase()}</span>
                                        </span>
                                    </React.Fragment>
                                ))}
                                <div className="flex-1" />
                                {/* Plan-limit usage chip — matches Content Studio's credits badge */}
                                <LimitBadge
                                    current={liveAccountsCount}
                                    limit={accountsCap}
                                    label={t('PLANS.LIMIT_BADGE.ACCOUNTS', 'accounts')}
                                    compact
                                />
                                <Button variant="outline" onClick={() => openConnectFor()}>
                                    <Plus />
                                    {t('SOCIAL_SCHEDULER.CONNECT_ACCOUNT', 'Connect account')}
                                    {isAtAccountsCap && <PremiumBadge />}
                                </Button>
                            </div>
                        )}

                        {/* Fully empty state */}
                        {isFullyEmpty && (
                            <div className={emptyBoxClass}>
                                <div className="mb-4">
                                    {t('SOCIAL_SCHEDULER.EMPTY_ACCOUNTS', 'No accounts connected. Connect your first social account.')}
                                </div>
                                <Button variant="outline" className="h-9" onClick={() => openConnectFor()}>
                                    <Plus />
                                    {t('SOCIAL_SCHEDULER.CONNECT_ACCOUNT', 'Connect account')}
                                    {isAtAccountsCap && <PremiumBadge />}
                                </Button>
                            </div>
                        )}

                        {/* Connected platform groups */}
                        {!isFullyEmpty && connectedPlatforms.map((platform) => {
                            const accountsForPlatform = accountsByPlatform[platform] || [];
                            return (
                                <div key={platform} className="mb-7">
                                    <div className="mb-3 flex items-center gap-2">
                                        <PlatformIcon platform={platform as SocialPlatform} size={20} />
                                        <h3 className="text-sm font-medium text-foreground">
                                            {platformConfig[platform as SocialPlatform]?.label || platform}
                                        </h3>
                                        <span className="min-w-[18px] bg-muted px-1.5 py-px text-center text-xs font-medium text-muted-foreground">
                                            {accountsForPlatform.length}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                                        {accountsForPlatform.map((acc) => (
                                            <AccountCard
                                                key={acc.uuid}
                                                account={acc}
                                                onPause={handlePause}
                                                onResume={handleResume}
                                                onDisconnect={handleDisconnect}
                                                getAffectedPosts={api.getAffectedPosts}
                                                postCount={postCountByAccount[acc.uuid] || 0}
                                            />
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => openConnectFor(platform as SocialPlatform)}
                                            aria-label={t('SOCIAL_SCHEDULER.ADD_ACCOUNT', 'Add account') || 'Add account'}
                                            className="flex min-h-[100px] flex-col items-center justify-center gap-1.5 border-[1.5px] border-dashed text-sm font-medium text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                        >
                                            <Plus className="size-5" />
                                            {t('SOCIAL_SCHEDULER.ADD_ACCOUNT', 'Add account')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Disconnected platforms — discovery CTAs */}
                        {!isFullyEmpty && disconnectedPlatforms.length > 0 && (
                            <div className="mt-8">
                                <div className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                                    {t('SOCIAL_SCHEDULER.EXPAND_REACH', 'Expand your reach')}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {disconnectedPlatforms.map((platform) => (
                                        <button
                                            key={platform}
                                            type="button"
                                            onClick={() => openConnectFor(platform)}
                                            className={cn(pillClass(false), 'text-foreground')}
                                        >
                                            <PlatformIcon platform={platform} size={20} />
                                            {platformConfig[platform]?.label || platform}
                                            <Plus className="size-3.5 text-muted-foreground" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Inspiration sources — read for Studio references, never posted to,
                            so they sit apart from the publishing accounts and take no slot. */}
                        <div className="mt-8">
                            <div className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                                {t('PINTEREST.SECTION_TITLE')}
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                                <PinterestConnectCard />
                            </div>
                        </div>
                    </div>
                );
            })()}

            <ConnectAccountDialog
                open={connectOpen}
                onClose={() => { setConnectOpen(false); setConnectPlatform(undefined); }}
                onConnect={handleConnect}
                initialPlatform={connectPlatform}
            />

            {/* Plan-cap reached / blocked-action feedback (shared component for consistency) */}
            <UpgradeNoticeSnackbar
                open={upgradeNotice.open}
                message={upgradeNotice.message}
                onClose={() => setUpgradeNotice((prev) => ({ ...prev, open: false }))}
            />

            {/* Delete post confirmation */}
            <Dialog open={!!pendingDeleteUuid} onOpenChange={(next) => { if (!next) setPendingDeleteUuid(null); }}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>{t('SOCIAL_SCHEDULER.DELETE_POST_TITLE', 'Delete post?')}</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                        {/* A live post is a different promise: it leaves the
                            platform too, and their followers stop seeing it. */}
                        {posts.find((p) => p.uuid === pendingDeleteUuid)?.status === 'published'
                            ? t(
                                'SOCIAL_SCHEDULER.DELETE_POST_MSG_PUBLISHED',
                                'This is live. Deleting removes it from the platform as well, and cannot be undone.',
                            )
                            : t('SOCIAL_SCHEDULER.DELETE_POST_MSG', 'This post will be permanently deleted. This action cannot be undone.')}
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" className="h-9" onClick={() => setPendingDeleteUuid(null)}>
                            {t('SOCIAL_SCHEDULER.CANCEL', 'Cancel')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={confirmDeletePost}
                            className="h-9 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                            {t('SOCIAL_SCHEDULER.DELETE_POST', 'Delete post')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </PageContainer>
    );
};
